/**
 * The end of a Play Now game: how each player's answers went, then every question with everyone's
 * answer. Players only, proven by the seat's resume token or a signed-in account on the seat; a
 * game that has no review (hosted, or the seat cannot be proven) renders nothing.
 */

import { useEffect, useState } from 'react';
import { api, type PlayNowReview } from '../lib/api';

export function GameReview({ code, token }: { code: string; token: string | null }) {
  const [review, setReview] = useState<PlayNowReview | null>(null);

  useEffect(() => {
    let live = true;
    api
      .getPlayNowReview(code, token)
      .then((r) => {
        if (live) setReview(r);
      })
      .catch(() => {
        /* no review for this game or this reader: the score stands on its own */
      });
    return () => {
      live = false;
    };
  }, [code, token]);

  if (!review || review.questions.length === 0) return null;
  const names = new Map(review.players.map((p) => [p.participantId, p.name]));
  // A Quick Game is one column; its name over every question says nothing.
  const showCategory = new Set(review.questions.map((q) => q.category)).size > 1;

  return (
    <div className="col pn-review">
      <h2>Right and wrong</h2>
      <table className="pn-tally">
        <thead>
          <tr>
            <th scope="col">Player</th>
            <th scope="col">Right</th>
            <th scope="col">Wrong</th>
            <th scope="col">No answer</th>
            <th scope="col">Speed bonus</th>
          </tr>
        </thead>
        <tbody>
          {review.players.map((p) => (
            <tr key={p.participantId}>
              <th scope="row">{p.name}</th>
              <td>{p.correct}</td>
              <td>{p.wrong}</td>
              <td>{p.unanswered}</td>
              <td>{p.speedBonus > 0 ? `+${p.speedBonus}` : 0}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <details className="pn-more">
        <summary>Review the questions</summary>
        <ol className="pn-questions">
          {review.questions.map((q) => (
            <li key={q.clueId} className="col">
              {showCategory && q.category ? <span className="faint">{q.category}</span> : null}
              <strong>{q.prompt}</strong>
              <span>
                Answer: <strong>{q.answer}</strong>
              </span>
              <ul className="pn-answers">
                {q.answers.map((a) => (
                  <li key={a.participantId}>
                    <span className="show-name">{names.get(a.participantId)}</span>
                    <span className={a.text === null ? 'faint' : undefined}>{a.text ?? 'No answer'}</span>
                    {a.verdict ? (
                      <span className={`tag ${a.verdict === 'correct' ? 'tag-good' : 'tag-bad'}`}>
                        {a.verdict === 'correct' ? 'Right' : 'Wrong'}
                      </span>
                    ) : null}
                    <span className="lb-score">{a.points > 0 ? `+${a.points}` : a.points}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}
